// frontend/src/pages/user/UserDashboardPage.js
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import useFetch from '../../hooks/useFetch'; 
import axios from 'axios'; 
import { 
    FaWallet, FaPiggyBank, FaChartLine, FaChartPie, FaList, FaBell, 
    FaDesktop, FaEye, FaCopy, FaUsers, FaExclamationTriangle,
    FaCalendarDay, FaCalendarWeek, FaCalendarAlt, FaHistory 
} from 'react-icons/fa';

// --- BİLEŞENLER ---

// Bakım/Uyarı Mesajı
const MaintenanceAlert = ({ message }) => (
    <div className="bg-yellow-500/10 border border-yellow-500/50 text-yellow-400 p-4 rounded-xl mb-6 flex items-center gap-3 animate-pulse">
        <FaExclamationTriangle size={24} />
        <span className="font-bold">{message}</span>
    </div>
);

// Bildirim (Toast)
const Toast = ({ message, onClose }) => (
    <div className="fixed bottom-5 right-5 bg-gray-900 border border-primary/50 text-white px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 z-50 animate-bounce-in">
        <div className="bg-primary/20 p-1 rounded-full text-primary">✓</div>
        <span className="text-sm font-medium">{message}</span>
        <button onClick={onClose} className="ml-2 text-gray-400 hover:text-white">✕</button>
    </div>
);

// İstatistik Kartı (Üst)
const StatCard = ({ title, value, icon, colorClass, subText }) => (
    <div className={`bg-gray-800 p-5 rounded-lg shadow-lg relative overflow-hidden border-l-4 ${colorClass} transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl group`}>
        <div className="flex items-start space-x-4 relative z-10">
            <div className="bg-gray-900 p-3 rounded-full flex-shrink-0 text-gray-400 group-hover:text-white transition-colors">
                {icon}
            </div>
            <div>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{title}</p>
                <p className="text-2xl font-bold text-white mt-1">{value}</p>
                {subText && <p className="text-[10px] text-gray-500 mt-1">{subText}</p>}
            </div>
        </div>
        <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/5 rounded-full blur-xl pointer-events-none group-hover:bg-white/10 transition-colors"></div>
    </div>
);

// Arama ve Filtreleme Çubuğu
const SearchFilterBar = ({ searchTerm, setSearchTerm, filterStatus, setFilterStatus }) => (
    <div className="flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-800 p-4 rounded-xl border border-gray-700 shadow-sm mb-6">
        <h2 className="text-lg font-bold text-white hidden sm:flex items-center gap-2">
            <FaList className="text-primary"/> Uygulamalarım
        </h2>
        <div className="flex items-center gap-3 w-full sm:w-auto">
             <div className="relative flex-grow">
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Uygulama ara..." className="w-full bg-gray-900 border border-gray-600 rounded-full py-2 pl-4 pr-4 text-sm focus:outline-none focus:border-primary text-white placeholder-gray-500 transition-colors" />
            </div>
            <div className="flex bg-gray-900 border border-gray-600 rounded-full p-0.5 flex-shrink-0">
                {['all', 'active', 'passive'].map(status => (
                    <button 
                        key={status}
                        onClick={() => setFilterStatus(status)} 
                        className={`px-3 py-1.5 text-xs font-bold rounded-full transition-all ${filterStatus === status ? 'bg-primary text-white shadow-md' : 'text-gray-400 hover:text-white'}`}
                    >
                        {status === 'all' ? 'Tümü' : status === 'active' ? 'Aktif' : 'Pasif'}
                    </button>
                ))}
            </div>
        </div>
    </div>
);

// Abonelik Kartı
const SubscriptionCard = ({ sub, onCopy }) => {
    const [showPass, setShowPass] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const emailVal = sub.loginCredential?.username || "";
    const passVal = sub.loginCredential?.password || "";
    
    const mask = (text) => {
        if (!text) return "";
        const parts = text.split('@');
        return parts.length === 2 ? `${parts[0][0]}****@${parts[1][0]}*******.${parts[1].split('.')[1]||'com'}` : text;
    };

    return (
        <div className={`bg-gray-800 rounded-lg shadow-lg p-5 flex flex-col justify-between border border-gray-700 relative transition-all duration-300 hover:border-primary/50 group ${!sub.isActive ? 'opacity-60 grayscale' : ''}`}>
            <div className={`absolute top-0 right-0 mt-3 -mr-1 px-3 py-1 text-[10px] font-bold text-white uppercase rounded-l-full shadow-md ${sub.isActive ? 'bg-green-600' : 'bg-gray-600'}`}>
                {sub.isActive ? 'AKTİF' : 'PASİF'}
            </div>
            <div>
                <div className="flex items-center mb-6">
                     {sub.iconUrl ? <img src={sub.iconUrl} alt={sub.name} className="h-10 w-10 object-contain mr-3 bg-white/10 rounded-lg p-1" /> : <div className="h-10 w-10 bg-gray-700 rounded-lg mr-3 flex items-center justify-center font-bold text-white">{sub.name.charAt(0)}</div>}
                    <h3 className="font-bold text-md text-white leading-tight">{sub.name}</h3>
                </div>
                <div className="space-y-3 mb-4">
                    <div>
                        <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">GİRİŞ E-POSTASI:</label>
                        <div className="flex items-center bg-gray-900 rounded border border-gray-600 p-1 mt-1 group-hover:border-gray-500 transition-colors">
                            <input readOnly value={showEmail ? emailVal : mask(emailVal)} className="flex-1 bg-transparent p-1.5 text-xs text-white focus:outline-none font-mono" />
                            <div className="flex pl-1">
                                <button onClick={() => setShowEmail(!showEmail)} className="p-1.5 text-gray-400 hover:text-primary">👁️</button>
                                <button onClick={() => onCopy(emailVal, sub.name, 'email')} className="p-1.5 text-gray-400 hover:text-green-400">📋</button>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">ŞİFRE:</label>
                        <div className="flex items-center bg-gray-900 rounded border border-gray-600 p-1 mt-1 group-hover:border-gray-500 transition-colors">
                            <input readOnly value={showPass ? passVal : "••••••••"} className="flex-1 bg-transparent p-1.5 text-xs text-white focus:outline-none font-mono" />
                            <div className="flex pl-1">
                                <button onClick={() => setShowPass(!showPass)} className="p-1.5 text-gray-400 hover:text-primary">👁️</button>
                                <button onClick={() => onCopy(passVal, sub.name, 'password')} className="p-1.5 text-gray-400 hover:text-green-400">📋</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="mt-2 pt-3 border-t border-gray-700 text-[10px] text-gray-400 flex justify-between font-bold">
                 <span className="text-primary bg-primary/10 px-2 py-1 rounded">Liste Fiyatı: ₺{sub.price}</span>
                 <span className="text-white/60 uppercase">{sub.renewalDate || "Her ayın 1'i"}</span>
            </div>
        </div>
    );
};

// --- SAĞ KOLON (GÜNCELLENDİ: TÜM İSTATİSTİKLER EKLENDİ) ---
const RightColumn = ({ myPayments, announcement, memberPayments, systemStats, correctUserCount, settings, activityStats }) => (
    <div className="space-y-6">
        
        {/* ÖDEMELERİM KARTI */}
        {settings?.myPaymentsCard?.show && (
            <div className="bg-gray-800 p-5 rounded-xl shadow-lg border border-gray-700">
                <h2 className="text-lg font-bold text-white mb-3 border-b border-gray-700 pb-2 flex items-center gap-2">
                    <FaWallet className="text-primary"/> Ödemelerim
                </h2>
                <div className="space-y-2 max-h-[250px] overflow-y-auto custom-scrollbar">
                    {myPayments?.length ? myPayments.map(p => (
                        <div key={p._id} className="flex justify-between items-center p-2.5 bg-gray-900 rounded border border-gray-700">
                            <div>
                                <p className="text-[10px] text-gray-400 uppercase font-bold">{new Date(p.paymentDate).toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' })}</p>
                                <p className="text-md font-bold text-white">₺{p.amount.toFixed(2)}</p>
                            </div>
                            <div className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${p.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : 'bg-green-500/20 text-green-500'}`}>
                                {p.status === 'pending' ? 'ÖDENECEK' : 'ÖDENDİ'}
                            </div>
                        </div>
                    )) : <div className="text-center py-4 text-xs text-gray-500">Kayıt yok.</div>}
                </div>
            </div>
        )}

        {/* SON DUYURU */}
        {settings?.visibility?.showAnnouncements && (
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-5 rounded-xl shadow-lg border border-gray-700 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-20 h-20 bg-primary/10 rounded-full -mr-10 -mt-10 blur-xl"></div>
                <h3 className="text-md font-bold text-white mb-2 flex items-center gap-2 relative z-10">
                    <span className="bg-primary/20 text-primary p-1 rounded"><FaBell size={12}/></span>
                    Son Duyuru
                </h3>
                <h4 className="font-semibold text-sm text-white relative z-10">{announcement?.title || "Duyuru Yok"}</h4>
                <p className="text-xs text-gray-400 mt-1 relative z-10">{announcement?.content || "Henüz duyuru eklenmedi."}</p>
            </div>
        )}

        {/* İSTATİSTİKLER (GELİŞTİRİLDİ) */}
        {settings?.statsCard?.show && (
            <div className="bg-gray-800 p-5 rounded-xl shadow-lg border border-gray-700">
                <h3 className="font-bold text-md text-white mb-3 flex items-center gap-2">
                    <FaChartLine className="text-blue-400"/> İstatistikler
                </h3>
                {systemStats ? (
                    <ul className="text-xs space-y-2 text-gray-400">
                        {settings.statsCard.showOnlineUsers && activityStats && (
                            <li className="flex justify-between border-b border-gray-700 pb-1">
                                <span className="flex items-center gap-2"><div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div> Online Kullanıcı:</span> 
                                <span className="font-bold text-white">{activityStats.onlineUsers?.length || 0}</span>
                            </li>
                        )}
                        {settings.statsCard.showTotalUsers && (
                            <li className="flex justify-between border-b border-gray-700 pb-1">
                                <span className="flex items-center gap-2"><FaUsers className="text-gray-500"/> Toplam Kullanıcı:</span> 
                                <span className="font-bold text-white">{correctUserCount || 0}</span>
                            </li>
                        )}
                        {settings.statsCard.showDailyViews && (
                            <li className="flex justify-between">
                                <span className="flex items-center gap-2"><FaCalendarDay className="text-blue-400"/> Görüntülenme (Günlük):</span> 
                                <span className="font-bold text-white">{systemStats.views?.daily || 0}</span>
                            </li>
                        )}
                        {settings.statsCard.showWeeklyViews && (
                            <li className="flex justify-between">
                                <span className="flex items-center gap-2"><FaCalendarWeek className="text-purple-400"/> Görüntülenme (Haftalık):</span> 
                                <span className="font-bold text-white">{systemStats.views?.weekly || 0}</span>
                            </li>
                        )}
                        {settings.statsCard.showMonthlyViews && (
                            <li className="flex justify-between">
                                <span className="flex items-center gap-2"><FaCalendarAlt className="text-orange-400"/> Görüntülenme (Aylık):</span> 
                                <span className="font-bold text-white">{systemStats.views?.monthly || 0}</span>
                            </li>
                        )}
                        {settings.statsCard.showAllTimeViews && (
                            <li className="flex justify-between">
                                <span className="flex items-center gap-2"><FaHistory className="text-red-400"/> Görüntülenme (Tüm Zamanlar):</span> 
                                <span className="font-bold text-white">{systemStats.views?.yearly || 0}</span> 
                            </li>
                        )}
                        {settings.statsCard.showCopyStats && (
                            <li className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                                <span className="flex items-center gap-2"><FaCopy className="text-green-400"/> Kopyalama İşlemi:</span> 
                                <span className="font-bold text-white">{systemStats.usage?.total || 0}</span>
                            </li>
                        )}
                    </ul>
                ) : <div className="text-center text-xs animate-pulse text-gray-500 py-4">Veriler Yükleniyor...</div>}
            </div>
        )}

        {/* SON İŞLEMLER */}
        {settings?.recentActivities?.show && (
            <div className="bg-gray-800 p-5 rounded-xl shadow-lg border border-gray-700">
                <h2 className="text-lg font-bold text-white mb-3 border-b border-gray-700 pb-2">Son İşlemler</h2>
                <div className="max-h-[250px] overflow-y-auto custom-scrollbar space-y-2">
                    {memberPayments.slice(0, settings.recentActivities.limit).map(p => {
                        let displayName = p.userId?.name || "Bilinmeyen";
                        
                        if (!settings.recentActivities.showFullNames) {
                            const parts = displayName.split(' ');
                            displayName = parts.map(part => part[0] + '***').join(' ');
                        }

                        return (
                            <div key={p._id} className="flex justify-between items-center py-2 border-b border-gray-700 last:border-0">
                                <div>
                                    <p className="text-xs font-bold text-gray-300">{displayName}</p>
                                    <p className="text-[10px] text-gray-500">{new Date(p.paymentDate).toLocaleDateString('tr-TR', {month:'2-digit', year:'numeric'})}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-bold text-white">₺{p.amount.toFixed(2)}</p>
                                    {settings.recentActivities.showStatus && (
                                        <p className={`text-[9px] font-bold ${p.status==='pending'?'text-yellow-500':'text-green-500'}`}>{p.status==='pending'?'Ödeme Bekleniyor':'Ödeme Yapıldı'}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        )}
    </div>
);

const UserDashboardPage = () => {
    const { userInfo } = useAuth();
    const API_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api';

    // VERİLER
    const { data: subscriptions, loading: subsLoading } = useFetch('/subscriptions');
    const { data: allPayments, loading: paymentsLoading } = useFetch('/payments');
    const { data: myStats, loading: statsLoading } = useFetch('/users/stats'); 
    const { data: systemStats } = useFetch('/extras/dashboard-stats');
    const { data: announcements } = useFetch('/extras/announcements'); 
    const { data: settings } = useFetch('/settings');
    const { data: activityStats } = useFetch('/extras/admin-stats');

    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [toast, setToast] = useState(null);
    const hasWelcomed = useRef(false);

    useEffect(() => {
        const speak = () => {
            if (userInfo?.name && !hasWelcomed.current && settings?.visibility?.showWelcomeMessage) {
                window.speechSynthesis.cancel(); 
                const text = `${userInfo.name}, Ortak Payda Noktasına hoş geldiniz.`;
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = 'tr-TR';
                utterance.rate = 0.9;
                window.speechSynthesis.speak(utterance);
                hasWelcomed.current = true;
            }
        };

        if (window.speechSynthesis.getVoices().length === 0) {
            window.speechSynthesis.onvoiceschanged = speak;
        } else {
            speak();
        }
    }, [userInfo, settings]);

    const mySubscriptions = useMemo(() => {
        if (!subscriptions || !userInfo) return [];
        return subscriptions.filter(s => s.allowedUserIds.includes(userInfo._id));
    }, [subscriptions, userInfo]);

    const filteredSubs = useMemo(() => {
        return mySubscriptions.filter(s => {
            const matchSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase());
            if (filterStatus === 'all') return matchSearch;
            if (filterStatus === 'active') return s.isActive && matchSearch;
            if (filterStatus === 'passive') return !s.isActive && matchSearch;
            return matchSearch;
        }).sort((a,b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1));
    }, [mySubscriptions, searchTerm, filterStatus]);

    const myPayments = useMemo(() => {
        if (!allPayments || !userInfo) return [];
        return allPayments.filter(p => p.userId._id === userInfo._id).sort((a,b) => new Date(b.paymentDate) - new Date(a.paymentDate));
    }, [allPayments, userInfo]);

    const handleCopy = async (text, appName, type) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setToast({message: "Kopyalandı"});
        setTimeout(() => setToast(null), 3000);
        try {
            const token = localStorage.getItem('token')?.replace(/"/g, '');
            if (token) {
                await axios.post(`${API_URL}/extras/log-activity`, 
                    { type: type === 'email' ? 'copy_email' : 'copy_password', details: `${appName} uygulamasının ${type === 'email' ? 'e-postası' : 'şifresi'} kopyalandı.` },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
        } catch (err) { console.error("Log error", err); }
    };

    if (subsLoading || paymentsLoading || statsLoading) return <div className="flex h-screen items-center justify-center text-white animate-pulse">Veriler Hazırlanıyor...</div>;

    const stats = myStats || { totalSystemUsers: 0, activeAppCount: 0, monthlyPayment: 0, monthlyGain: 0, yearlyPayment: 0, yearlyGain: 0 };

    return (
        <div className="pb-10">
            {toast && <Toast message={toast.message} onClose={() => setToast(null)} />}
            
            <header className="mb-6">
                <h1 className="text-2xl font-bold text-white">Hoş Geldin, <span className="text-primary">{userInfo?.name}</span>!</h1>
                <p className="text-gray-400 text-sm">Finansal özetin aşağıdadır.</p>
            </header>

            {settings?.visibility?.maintenanceMessage && (
                <MaintenanceAlert message={settings.visibility.maintenanceMessage} />
            )}

            {settings?.visibility?.showFinancialCards && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                    <StatCard title="TOPLAM ÜYE (SİSTEM)" value={stats.totalSystemUsers || 0} colorClass="border-blue-500" icon={<FaUsers />} />
                    <StatCard title="AKTİF UYGULAMAM" value={stats.activeAppCount} colorClass="border-green-500" icon={<FaDesktop />} />
                    <StatCard title="AYLIK TOPLAM TUTAR" value={`₺${stats.monthlyPayment.toFixed(2)} TL`} colorClass="border-red-500" icon={<FaWallet />} subText="Sizin payınıza düşen aylık gider."/>
                    <StatCard title="AYLIK KAZANÇ" value={`₺${stats.monthlyGain.toFixed(2)} TL`} colorClass="border-yellow-500" icon={<FaPiggyBank />} subText="Bireysel alsaydınız ödeyeceğiniz fark."/>
                    <StatCard title="YILLIK TOPLAM TUTAR" value={`₺${stats.yearlyPayment.toFixed(2)} TL`} colorClass="border-purple-500" icon={<FaChartPie />} subText="Sizin payınıza düşen yıllık gider."/>
                    <StatCard title="YILLIK KAZANÇ" value={`₺${stats.yearlyGain.toFixed(2)} TL`} colorClass="border-teal-500" icon={<FaChartLine />} subText="Bireysel alsaydınız ödeyeceğiniz yıllık fark."/>
                </div>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 order-2 xl:order-1">
                    <SearchFilterBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} filterStatus={filterStatus} setFilterStatus={setFilterStatus} />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredSubs.length > 0 ? (
                            filteredSubs.map(sub => (
                                <SubscriptionCard key={sub._id} sub={sub} onCopy={handleCopy} />
                            ))
                        ) : (
                            <div className="col-span-2 text-center py-10 text-gray-500 border border-dashed border-gray-700 rounded-xl">Uygulama bulunamadı.</div>
                        )}
                    </div>
                </div>

                <div className="xl:col-span-1 order-1 xl:order-2">
                    <RightColumn 
                        myPayments={myPayments} 
                        announcement={announcements?.[announcements.length - 1]} 
                        memberPayments={allPayments || []} 
                        systemStats={systemStats} 
                        correctUserCount={stats.totalSystemUsers}
                        settings={settings} 
                        activityStats={activityStats} 
                    />
                </div>
            </div>
        </div>
    );
};

export default UserDashboardPage;